/**
 * ActivityTypeFilter - "Filter" button + popover for filtering activities by type
 */

import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal, Check } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { ActivityType } from '../../types/activity';
import { ACTIVITY_TYPES, ACTIVITY_TYPE_CONFIG } from '../../utils/activityTypes';

const FILTER_OPTIONS: { type: ActivityType; label: string; color: string }[] = ACTIVITY_TYPES.map((type) => ({
  type,
  label: ACTIVITY_TYPE_CONFIG[type].label,
  color: ACTIVITY_TYPE_CONFIG[type].iconColor,
}));

interface ActivityTypeFilterProps {
  selectedTypes: ActivityType[];
  onChange: (types: ActivityType[]) => void;
  align?: 'left' | 'right';
  className?: string;
}

export default function ActivityTypeFilter({
  selectedTypes,
  onChange,
  align = 'right',
  className,
}: ActivityTypeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);

  useClickOutside(triggerRef, () => setIsOpen(false), isOpen);

  const handleOpen = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        ...(align === 'right' ? { right: window.innerWidth - rect.right } : { left: rect.left }),
        zIndex: 9999,
      });
    }
    setIsOpen((prev) => !prev);
  };

  const toggleType = (type: ActivityType) => {
    onChange(
      selectedTypes.includes(type)
        ? selectedTypes.filter((t) => t !== type)
        : [...selectedTypes, type]
    );
  };

  const hasActiveFilter = selectedTypes.length > 0;

  return (
    <div className={`relative${className ? ` ${className}` : ''}`} ref={triggerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${
          hasActiveFilter
            ? 'border-[#0dccf2] text-[#0dccf2] bg-[#0dccf2]/5'
            : 'border-slate-200 text-slate-500 hover:bg-slate-50'
        }`}
      >
        <SlidersHorizontal size={14} />
        Filter
        {hasActiveFilter && (
          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#0dccf2] text-white text-[10px] font-bold">
            {selectedTypes.length}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div
          className="w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2"
          style={menuStyle}
        >
          <div className="flex items-center justify-between px-3 pb-1.5">
            <span className="text-xs font-semibold text-slate-400">依種類篩選</span>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-[#0dccf2] font-medium hover:underline"
              >
                清除
              </button>
            )}
          </div>
          {FILTER_OPTIONS.map((option) => {
            const isSelected = selectedTypes.includes(option.type);
            return (
              <button
                key={option.type}
                type="button"
                onClick={() => toggleType(option.type)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <span
                  className={`flex items-center justify-center w-4 h-4 rounded border shrink-0 ${
                    isSelected ? 'border-transparent' : 'border-slate-300'
                  }`}
                  style={isSelected ? { background: option.color } : undefined}
                >
                  {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
                </span>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: option.color }} />
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
